import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface AIAssistantProps {
  selectedDate: string;
  onTasksUpdated?: () => void;
}

interface Suggestion {
  taskId: string;
  taskTitle: string;
  suggestedHour: number;
  reason: string;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ selectedDate, onTasksUpdated }) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [reflection, setReflection] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'schedule' | 'reflection'>('schedule');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    checkAIStatus();
  }, []);

  const checkAIStatus = async () => {
    try {
      const response = await apiService.getAIStatus();
      setIsAvailable(response.available);
    } catch (err) {
      setIsAvailable(false);
    }
  };

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const handleGetSuggestions = async () => {
    setLoading(true);
    setError('');
    setSuggestions([]);

    try {
      const response = await apiService.suggestSchedule(selectedDate);
      if (response.success) {
        setSuggestions(response.suggestions || []);
        if (response.suggestions?.length === 0) {
          setError('No unscheduled tasks to optimize. Add some tasks first!');
        }
      } else {
        setError(response.error || 'Failed to get suggestions');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to connect to AI service');
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestions = async () => {
    if (suggestions.length === 0) return;

    setApplying(true);
    try {
      const response = await apiService.applyScheduleSuggestions(suggestions);
      if (response.success) {
        setSuggestions([]);
        if (onTasksUpdated) {
          onTasksUpdated();
        }
        setError(`Applied ${response.applied} scheduling suggestions!`);
      } else {
        setError(response.error || 'Failed to apply suggestions');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply suggestions');
    } finally {
      setApplying(false);
    }
  };

  const handleGetReflection = async () => {
    setLoading(true);
    setError('');
    setReflection(null);

    try {
      const response = await apiService.getDailyReflection(selectedDate);
      if (response.success) {
        setReflection(response.reflection);
        setStats(response.stats);
      } else {
        setError(response.error || 'Failed to generate reflection');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to connect to AI service');
    } finally {
      setLoading(false);
    }
  };

  if (!isAvailable) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#fef3c7',
        borderRadius: '12px',
        border: '1px solid #fcd34d',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>🤖</span>
          <div>
            <h4 style={{ margin: '0 0 4px 0', color: '#92400e', fontWeight: 600 }}>AI Assistant</h4>
            <p style={{ margin: 0, color: '#a16207', fontSize: '0.875rem' }}>
              AI features require a Gemini API key. Add GEMINI_API_KEY to your environment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      marginBottom: '20px'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '16px 20px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>🤖</span>
          <div>
            <h4 style={{ margin: 0, fontWeight: 600 }}>AI Assistant</h4>
            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>Powered by Gemini</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        <button
          onClick={() => setActiveTab('schedule')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            backgroundColor: activeTab === 'schedule' ? '#f3f4f6' : 'white',
            borderBottom: activeTab === 'schedule' ? '2px solid #667eea' : '2px solid transparent',
            fontWeight: activeTab === 'schedule' ? 600 : 400,
            color: activeTab === 'schedule' ? '#667eea' : '#6b7280',
            cursor: 'pointer'
          }}
        >
          Auto-Schedule
        </button>
        <button
          onClick={() => setActiveTab('reflection')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            backgroundColor: activeTab === 'reflection' ? '#f3f4f6' : 'white',
            borderBottom: activeTab === 'reflection' ? '2px solid #667eea' : '2px solid transparent',
            fontWeight: activeTab === 'reflection' ? 600 : 400,
            color: activeTab === 'reflection' ? '#667eea' : '#6b7280',
            cursor: 'pointer'
          }}
        >
          Daily Reflection
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: error.includes('Applied') ? '#f0fdf4' : '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
            color: error.includes('Applied') ? '#16a34a' : '#dc2626',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        {activeTab === 'schedule' && (
          <>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '16px' }}>
              AI will analyze your unscheduled tasks and suggest optimal time slots based on priorities and duration.
            </p>

            <button
              onClick={handleGetSuggestions}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: loading ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginBottom: '16px'
              }}
            >
              {loading ? 'Analyzing...' : 'Get Smart Suggestions'}
            </button>

            {suggestions.length > 0 && (
              <div>
                <h5 style={{ margin: '0 0 12px 0', color: '#374151' }}>Suggestions:</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {suggestions.map((s, i) => (
                    <div key={i} style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      borderLeft: '3px solid #667eea'
                    }}>
                      <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                        {s.taskTitle}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        Schedule at <strong>{formatHour(s.suggestedHour)}</strong>
                      </div>
                      {s.reason && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>
                          {s.reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleApplySuggestions}
                  disabled={applying}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#10b981',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: 600,
                    cursor: applying ? 'not-allowed' : 'pointer'
                  }}
                >
                  {applying ? 'Applying...' : 'Apply All Suggestions'}
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'reflection' && (
          <>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '16px' }}>
              Get an AI-generated reflection on your day's progress with insights and suggestions for tomorrow.
            </p>

            <button
              onClick={handleGetReflection}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: loading ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginBottom: '16px'
              }}
            >
              {loading ? 'Generating...' : 'Generate Reflection'}
            </button>

            {stats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>{stats.completed}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Completed</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626' }}>{stats.pending}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Pending</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2563eb' }}>{stats.completionRate}%</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Rate</div>
                </div>
              </div>
            )}

            {reflection && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                borderLeft: '3px solid #667eea'
              }}>
                <p style={{ margin: 0, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                  {reflection}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
